import { getAppInsightsInstance, telemetry } from '@/services/telemetry/telemetry';
import type {
  SupportedDatePattern,
  SupportedLocale,
  WarningCode,
} from '@config/localizationConfig';

// ---------------------------------------------------------------------------
// Public discriminated-union payload type
// See specs/105-ui-localization-config/contracts/localization-warnings.contract.md
// ---------------------------------------------------------------------------

export type LocalizationWarningPayload =
  | {
      readonly code: 'LOCALIZATION_CONFIG_FIELD_MISSING';
      readonly field: 'language' | 'languageOverrideEnabled' | 'dateFormatPattern';
      readonly defaultApplied: SupportedLocale | SupportedDatePattern | boolean;
    }
  | {
      readonly code: 'LOCALIZATION_UNSUPPORTED_LANGUAGE';
      readonly configured: string;
      readonly fallback: SupportedLocale;
    }
  | {
      readonly code: 'LOCALIZATION_INVALID_DATE_PATTERN';
      readonly configured: string;
      readonly fallback: SupportedDatePattern;
    }
  | {
      readonly code: 'LOCALIZATION_OVERRIDE_BLOCKED_LANGUAGE_CHANGE';
      readonly attempted: string;
      readonly active: SupportedLocale;
    };

// Component name used as the App Insights `componentName` value for every
// warning emitted from this module.
const COMPONENT_NAME = 'Localization';

// Once-per-process dedupe set for INVALID_DATE_PATTERN per data-model.md §3
// (one warning per malformed pattern value).
const emittedInvalidDatePatterns = new Set<string>();

// Build the human-readable message for a given warning payload.
function buildMessage(payload: LocalizationWarningPayload): string {
  switch (payload.code) {
    case 'LOCALIZATION_CONFIG_FIELD_MISSING':
      return `Localization config field "${payload.field}" missing or malformed; applied default ${JSON.stringify(payload.defaultApplied)}.`;
    case 'LOCALIZATION_UNSUPPORTED_LANGUAGE':
      return `Configured language "${payload.configured}" is not supported; fell back to "${payload.fallback}".`;
    case 'LOCALIZATION_INVALID_DATE_PATTERN':
      return `Configured dateFormatPattern "${payload.configured}" is not supported; fell back to "${payload.fallback}".`;
    case 'LOCALIZATION_OVERRIDE_BLOCKED_LANGUAGE_CHANGE':
      return `Language change to "${payload.attempted}" blocked by config override; active language remains "${payload.active}".`;
  }
}

// Convert the payload to a plain Record<string,string> for App Insights properties.
function toProperties(payload: LocalizationWarningPayload): Record<string, string> {
  const result: Record<string, string> = { code: payload.code };
  for (const [key, value] of Object.entries(payload)) {
    if (key === 'code') continue;
    result[key] = typeof value === 'string' ? value : String(value);
  }
  return result;
}

/**
 * Emit a localization warning to App Insights via the existing telemetry
 * wrapper if available; otherwise log to `console.warn`. Always swallows
 * exceptions so warning emission never breaks the caller. Per-process dedup
 * applies only to `LOCALIZATION_INVALID_DATE_PATTERN` (one emission per
 * unique malformed pattern value).
 */
export function emitLocalizationWarning(payload: LocalizationWarningPayload): void {
  try {
    if (payload.code === 'LOCALIZATION_INVALID_DATE_PATTERN') {
      if (emittedInvalidDatePatterns.has(payload.configured)) return;
      emittedInvalidDatePatterns.add(payload.configured);
    }

    const message = buildMessage(payload);
    const props = toProperties(payload);
    const appInsights = getAppInsightsInstance();

    if (appInsights) {
      try {
        telemetry.trackWarning(COMPONENT_NAME, payload.code, message, props);
        return;
      } catch {
        // fall through to console.warn fallback
      }
    }

    try {
      console.warn(`[${COMPONENT_NAME}]`, payload.code, message, props);
    } catch {
      // swallow — warning emission must never break the caller
    }
  } catch {
    // swallow — outer safety net
  }
}

// Test-only helper to clear per-process dedup state between cases.
// Exported under a `__` prefix to discourage production use.
export function __resetLocalizationWarningDedupForTests(): void {
  emittedInvalidDatePatterns.clear();
}

// Re-export the WarningCode union for convenience.
export type { WarningCode };
